import { keyframes, styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { HEADER } from 'src/config-global';
import { authFormEntrance } from 'src/layouts/auth-form-animation';

const ARTWORK_ENTRANCE_DURATION = 650;
const HEADING_ENTRANCE_DELAY = 350;

const welcomeEntrance = keyframes`
  from { opacity: 0; transform: translateY(2%); }
  to { opacity: 1; transform: translateY(0); }
`;

const headingEntrance = keyframes`
  from { opacity: 0; transform: translateY(35%); filter: blur(0.1em); }
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

export const LoginPageRoot = styled('div')(({ theme }) => ({
  minHeight: '100svh',
  backgroundColor: theme.palette.background.default,
  paddingTop: HEADER.H_MOBILE,
  [theme.breakpoints.up('lg')]: {
    paddingTop: HEADER.H_MAIN_DESKTOP,
  },
}));

export const LoginMain = styled('main')(({ theme }) => ({
  display: 'grid',
  backgroundColor: theme.palette.background.neutral,
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateRows: '1fr',
  minHeight: `calc(100svh - ${HEADER.H_MOBILE}px)`,
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
    gridTemplateRows: '1fr',
    minHeight: `calc(100svh - ${HEADER.H_MAIN_DESKTOP}px)`,
  },
}));

export const WelcomePanel = styled('section')(({ theme }) => ({
  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  minWidth: 0,
  height: theme.spacing(16),
  margin: theme.spacing(2, 2, 0),
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.background.neutral,
  color: theme.palette.text.primary,
  [theme.breakpoints.up('md')]: {
    height: theme.spacing(18),
    margin: theme.spacing(3, 4, 0),
  },
  [theme.breakpoints.up('lg')]: {
    position: 'sticky',
    top: `calc(${HEADER.H_MAIN_DESKTOP}px + ${theme.spacing(3)})`,
    alignSelf: 'start',
    height: `calc(100svh - ${HEADER.H_MAIN_DESKTOP}px - ${theme.spacing(6)})`,
    minHeight: theme.spacing(56),
    margin: theme.spacing(3, 0, 3, 3),
    borderRadius: theme.spacing(4),
  },
}));

export const WelcomeHeading = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(5, 5, 0),
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(6, 6, 0),
  },
}));

export const WelcomeHeadingLine = styled('span')({
  display: 'inline-block',
  animation: `${headingEntrance} 600ms cubic-bezier(0.16, 1, 0.3, 1) both`,
  animationDelay: `${HEADING_ENTRANCE_DELAY}ms`,
  animationPlayState: 'var(--welcome-animation-play-state, paused)',
  '&:last-child': {
    animationDelay: `${HEADING_ENTRANCE_DELAY + 140}ms`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
});

export const WelcomeArtwork = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '62%',
  height: '125%',
  right: '-15%',
  bottom: '-10%',
  animation: `${welcomeEntrance} ${ARTWORK_ENTRANCE_DURATION}ms ease-out both`,
  animationPlayState: 'var(--welcome-animation-play-state, paused)',
  pointerEvents: 'none',
  maskImage:
    'linear-gradient(to bottom, transparent, black 12%, black 92%, transparent), linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
  maskComposite: 'intersect',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  [theme.breakpoints.up('md')]: {
    width: '55%',
    height: '135%',
    right: '-2%',
    bottom: '-12%',
  },
  [theme.breakpoints.up('lg')]: {
    width: '106%',
    maxWidth: theme.spacing(110),
    height: 'auto',
    aspectRatio: '3 / 2',
    left: '-3%',
    right: '-3%',
    margin: '0 auto',
    bottom: 0,
  },
}));

export const LoginFormColumn = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minWidth: 0,
  padding: theme.spacing(3, 2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    justifyContent: 'center',
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(5, 6),
  },
}));

export const LoginFormSurface = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: theme.spacing(60),
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  boxShadow: theme.customShadows.z8,
  animation: `${authFormEntrance} 560ms cubic-bezier(0.16, 1, 0.3, 1) 180ms both`,
  animationPlayState: 'var(--welcome-animation-play-state, paused)',
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    borderRadius: theme.spacing(4),
  },
}));
